import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Pagination,
  Tooltip,
} from '@ads/components-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  ListBulletIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentPersonViewModel } from '@/shared/api/generated';
import { ERole } from '@/shared/lib/roles';
import { usePermissions } from '@/shared/store/hooks/usePermissions';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

import { useIndividualPaymentContracts } from '../../../hooks/useIndividualPaymentContracts';
import type { IndividualPaymentContractAction } from '../../../types/individualPayment';
import type { ContractViewer } from '../../../utils/contractActionAvailability';
import { isContractActionAvailable } from '../../../utils/contractActionAvailability';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { ContractStatusBadge } from '../../atoms/ContractStatusBadge';
import { IndividualPaymentSearchPrompt } from '../../molecules/IndividualPaymentSearchPrompt';

export interface IndividualPaymentTableProps {
  readonly people: readonly IndividualPaymentPersonViewModel[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly hasSearched: boolean;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
  readonly rangeFrom: number;
  readonly rangeTo: number;
  /** Total de registros quando o backend informa; `null` quando desconhecido. */
  readonly totalCount: number | null;
  /** Total de páginas quando o backend informa; `null` quando desconhecido. */
  readonly totalPages: number | null;
  /** Página 0-based, usada para recolher as linhas ao paginar. */
  readonly pageIndex: number;
  /** Mesmo contrato de `EnrollmentTable` — o `Pagination` do ADS exige um setter de state. */
  readonly onPageChange: Dispatch<SetStateAction<number>>;
  readonly onRetry: () => void;
  /**
   * Ação escolhida no menu "Gerenciar" da linha-filha, com o `financialPersonId`
   * do contrato (campo `id` da linha-filha) e o contexto da linha-pai.
   */
  readonly onContractAction: (
    action: IndividualPaymentContractAction,
    financialPersonId: number,
    context: ContractActionContext,
  ) => void;
}

/**
 * Contexto da linha-pai, entregue junto da ação do contrato.
 *
 * O `financialPersonId` sozinho identifica o contrato, mas não a pessoa: para
 * invalidar a lista de contratos certa depois de inativar, e para pré-preencher
 * o aluno ao criar outro plano, quem age precisa saber de quem é a linha.
 */
export interface ContractActionContext {
  /** Pessoa dona do contrato. */
  readonly personId: number;
  /** Nome exibido na linha-pai — evita uma busca só para reexibi-lo. */
  readonly personName: string;
  /** Curso do contrato, para o detalhe da confirmação. */
  readonly courseName: string | null;
  /** Valor total do contrato, para o detalhe da confirmação. */
  readonly totalValue: number | null;
}

/** Assinatura dos ícones do Hero Icons — todos compartilham este tipo. */
type HeroIcon = typeof RectangleStackIcon;

/**
 * Itens do menu "Gerenciar", na ordem exibida.
 *
 * `labelKey` é literal (`as const`) para que `t()` valide cada chave contra o
 * JSON `pt-BR` em tempo de compilação.
 */
const CONTRACT_ACTION_ITEMS = [
  {
    action: 'paymentConsolidation',
    labelKey: 'actions.paymentConsolidation',
    icon: RectangleStackIcon,
  },
  {
    action: 'editIndividualPayment',
    labelKey: 'actions.editIndividualPayment',
    icon: PencilSquareIcon,
  },
  { action: 'viewParcels', labelKey: 'actions.viewParcels', icon: ListBulletIcon },
  {
    action: 'addIndividualPayment',
    labelKey: 'actions.addIndividualPayment',
    icon: PlusCircleIcon,
  },
  {
    action: 'deactivateFinancial',
    labelKey: 'actions.deactivateFinancial',
    icon: NoSymbolIcon,
    isDestructive: true,
  },
] as const satisfies readonly {
  readonly action: IndividualPaymentContractAction;
  readonly labelKey: string;
  readonly icon: HeroIcon;
  readonly isDestructive?: boolean;
}[];

/**
 * Grid de colunas compartilhado entre cabeçalho, linha-pai e linha-filha.
 *
 * São seis colunas, não uma por campo: os pares relacionados (tipo/data e
 * método/valor) ficam empilhados na mesma célula — primária em cima, secundária
 * embaixo. Com nove colunas a tabela só cabia com scroll horizontal e todo
 * valor longo saía truncado; assim ela fecha em ~56rem e nada é cortado.
 *
 * As colunas de texto livre usam `minmax(<min>,<fr>)` para crescer com a tela
 * sem encolher abaixo do legível. O alinhamento depende de cabeçalho, pai e
 * filho usarem exatamente esta mesma string **e** o mesmo `ROW_PADDING_X`.
 */
const ROW_GRID =
  'grid grid-cols-[2.5rem_minmax(18rem,2fr)_minmax(9rem,0.8fr)_minmax(11rem,1fr)_8.5rem_3rem] gap-x-xs';

/** Padding horizontal comum às três linhas — sem ele as colunas desalinham. */
const ROW_PADDING_X = 'px-mini';

/** Largura mínima antes do scroll horizontal (soma dos mínimos das colunas). */
const MIN_TABLE_WIDTH = 'min-w-[56rem]';

/** Casca do card, idêntica à de `EnrollmentTable`. */
const CARD_CLASS =
  'overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs';

/** Conjunto vazio estável — evita recriar a cada render ao trocar de página. */
const EMPTY_IDS: ReadonlySet<number> = new Set();

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length <= 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

function TableHeader() {
  const { t } = useTranslation('individualPayment');
  return (
    <div
      className={`${ROW_GRID} ${ROW_PADDING_X} sticky top-none items-center border-b-hairline border-ctx-layout-border bg-ctx-layout-surface py-micro`}
    >
      <div />
      <span className="ts-subtitle-xxs text-ctx-content-title">{t('table.columns.student')}</span>
      <span className="ts-subtitle-xxs text-ctx-content-title">
        {t('table.columns.typeAndDate')}
      </span>
      <span className="ts-subtitle-xxs text-ctx-content-title">{t('table.columns.payment')}</span>
      <span className="ts-subtitle-xxs text-ctx-content-title">{t('table.columns.status')}</span>
      <span className="sr-only">{t('table.columns.actions')}</span>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-ctx-layout-border">
      {Array.from({ length: 5 }, (_, index) => `skeleton-${index}`).map(key => (
        <div key={key} className={`${ROW_GRID} ${ROW_PADDING_X} items-center py-xxs`}>
          <Skeleton className="h-xxs w-xxs rounded-sm" />
          <div className="flex flex-col gap-quark">
            <Skeleton className="h-xs w-[14rem] rounded-sm" />
            <Skeleton className="h-xs w-[9rem] rounded-sm" />
          </div>
          <div className="flex flex-col gap-quark">
            <Skeleton className="h-xs w-[5rem] rounded-sm" />
            <Skeleton className="h-xs w-[6rem] rounded-sm" />
          </div>
          <div className="flex flex-col gap-quark">
            <Skeleton className="h-xs w-[8rem] rounded-sm" />
            <Skeleton className="h-xs w-[6rem] rounded-sm" />
          </div>
          <Skeleton className="h-xs w-[5rem] rounded-sm" />
          <Skeleton className="h-xs w-xs rounded-sm" />
        </div>
      ))}
    </div>
  );
}

function ContractRow({
  contract,
  viewer,
  onAction,
}: {
  contract: {
    readonly courseName?: string | null;
    /** Descrição do contrato (ex.: turma) — exibida na linha secundária. */
    readonly description?: string | null;
    readonly typeText?: string | null;
    readonly creationDate?: string | null;
    readonly poloName?: string | null;
    readonly paymentMethod?: string | null;
    readonly totalValue?: number | null;
    readonly status?: string | null;
    /** Contrato de curso USP — só neles o legado oferece a consolidação. */
    readonly isUSP?: boolean;
    /**
     * Status numérico do contrato financeiro (`ECourseFinancialPersonStatus`).
     *
     * Distinto do `status` acima, que é o texto exibido. É por ele que a régua
     * decide esconder a inativação de um contrato já inativo.
     */
    readonly statusId?: number | null;
  };
  /** Papéis do operador — decidem quais ações a linha oferece. */
  viewer: ContractViewer;
  onAction: (action: IndividualPaymentContractAction) => void;
}) {
  const { t } = useTranslation('individualPayment');

  const courseName = contract.courseName ?? EMPTY_VALUE;
  const paymentMethod = contract.paymentMethod ?? EMPTY_VALUE;

  // As condições de cada item vêm do grid legado e vivem em
  // `isContractActionAvailable`, com teste próprio.
  const contractActions = CONTRACT_ACTION_ITEMS.filter(item =>
    isContractActionAvailable(item.action, contract, viewer),
  );

  // Descrição e polo dividem a linha secundária do contrato — sozinhos não
  // justificavam uma coluna cada, e juntos evitam o scroll horizontal.
  const secondaryLine =
    [
      contract.description,
      contract.poloName ? t('contracts.poloLabel', { polo: contract.poloName }) : null,
    ]
      .filter(Boolean)
      .join('  ·  ') || EMPTY_VALUE;

  return (
    <div
      className={`${ROW_GRID} ${ROW_PADDING_X} min-h-[4rem] items-center py-micro transition-colors hover:bg-ctx-interactive-secondaryHover`}
    >
      <div className="flex items-center justify-center">
        <DocumentTextIcon className="h-xxs w-xxs shrink-0 text-ctx-content-placeholder" />
      </div>

      {/* Contrato: curso em cima, descrição e polo como linha secundária. */}
      <div className="flex min-w-none flex-col">
        <Tooltip title={courseName}>
          <span className="truncate ts-paragraph-xxs !font-medium text-ctx-content-title">
            {courseName}
          </span>
        </Tooltip>
        <Tooltip title={secondaryLine}>
          <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
            {secondaryLine}
          </span>
        </Tooltip>
      </div>

      {/* Tipo / Data */}
      <div className="flex min-w-none flex-col">
        <span className="truncate ts-paragraph-xxs text-ctx-content-base">
          {contract.typeText ?? EMPTY_VALUE}
        </span>
        <span className="ts-caption-xxs tabular-nums text-ctx-content-placeholder">
          {formatContractDate(contract.creationDate)}
        </span>
      </div>

      {/* Pagamento: valor em destaque, método como linha secundária. */}
      <div className="flex min-w-none flex-col">
        <span className="ts-paragraph-xxs !font-semibold tabular-nums text-ctx-content-title">
          {formatCurrency(contract.totalValue)}
        </span>
        <Tooltip title={paymentMethod}>
          <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
            {paymentMethod}
          </span>
        </Tooltip>
      </div>

      <div className="flex min-w-none items-center">
        <ContractStatusBadge status={contract.status} />
      </div>

      <div className="flex items-center">
        <Dropdown
          align="end"
          trigger={
            <IconButton
              ariaLabel={t('actions.manage')}
              hierarchy="secondary"
              size="sm"
              icon={EllipsisVerticalIcon}
            />
          }
        >
          {contractActions.map(item => (
            <DropdownItem
              key={item.action}
              onSelect={() => onAction(item.action)}
              {...('isDestructive' in item ? { status: 'destructive' as const } : {})}
            >
              <DropdownItemContent custom={{ content: 'text-nowrap' }} leadingIcon={item.icon}>
                {t(item.labelKey)}
              </DropdownItemContent>
            </DropdownItem>
          ))}
        </Dropdown>
      </div>
    </div>
  );
}

function ContractRowsSection({
  personId,
  personName,
  isOpen,
  onContractAction,
}: {
  personId: number;
  personName: string;
  isOpen: boolean;
  onContractAction: IndividualPaymentTableProps['onContractAction'];
}) {
  const { t } = useTranslation('individualPayment');
  const { contracts, isLoading, isFetching, isError } = useIndividualPaymentContracts(
    personId,
    isOpen,
  );

  // Os papéis decidem quais ações a linha oferece — regras herdadas do grid
  // legado. Lidos aqui, uma vez por aluno expandido, em vez de por contrato.
  const { hasRole } = usePermissions();
  const viewer: ContractViewer = { isSecretary: hasRole(ERole.Secretary) };

  if (!isOpen) return null;

  // A seção-filha se distingue pelo fundo (`bg-ctx-layout-body`) e não por um
  // card recuado: qualquer padding horizontal extra aqui desalinharia as colunas
  // do cabeçalho.
  if (isLoading) {
    return (
      <div className="border-b-hairline border-ctx-layout-border bg-ctx-layout-body">
        <div className={`${ROW_GRID} ${ROW_PADDING_X} min-h-[4rem] items-center py-micro`}>
          <div />
          <Skeleton className="h-xs w-[12rem] rounded-sm" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-xxs ts-caption-xxs text-ctx-interactive-destructive">
        {t('contracts.error')}
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-xxs ts-caption-xxs text-ctx-content-placeholder">
        {t('contracts.empty')}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col divide-y divide-ctx-layout-border border-b-hairline border-ctx-layout-border bg-ctx-layout-body${
        isFetching ? ' opacity-medium' : ''
      }`}
    >
      {contracts.map(contract => (
        <ContractRow
          key={contract.id}
          contract={contract}
          viewer={viewer}
          onAction={action =>
            onContractAction(action, contract.id ?? 0, {
              personId,
              personName,
              courseName: contract.courseName ?? null,
              totalValue: contract.totalValue ?? null,
            })
          }
        />
      ))}
    </div>
  );
}

function PersonRow({
  person,
  isOpen,
  onToggle,
}: {
  person: IndividualPaymentPersonViewModel;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('individualPayment');
  const name = person.name ?? EMPTY_VALUE;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={isOpen ? t('table.collapse') : t('table.expand')}
      className={`${ROW_GRID} ${ROW_PADDING_X} group min-h-[4rem] w-full content-center items-center border-b-hairline border-ctx-layout-border py-xxxs text-left transition-colors ${
        isOpen
          ? 'bg-ctx-interactive-secondary hover:bg-ctx-interactive-secondaryHover'
          : 'bg-ctx-layout-surface hover:bg-ctx-layout-body'
      }`}
    >
      {/* Ícone, não `IconButton`: a linha inteira já é o `<button>` que expande,
          e um botão dentro de outro é HTML inválido. */}
      <div className="flex items-center justify-center border-hairline h-xs w-xs rounded-sm border-ctx-layout-border group-hover:border-ctx-interactive-secondaryHover">
        <ChevronRightIcon
          className={`h-mini w-mini text-ctx-content-placeholder transition-transform ${
            isOpen ? 'rotate-90' : ''
          }`}
        />
      </div>

      <div className="flex min-w-none items-center gap-xxs">
        <Avatar fallback={getInitials(name)} size="md" />
        {/* Sem `truncate`: o nome do aluno é exibido por inteiro, quebrando em
            mais de uma linha quando necessário (daí o `min-h` na linha). */}
        <span className="break-words ts-paragraph-xxs !font-semibold text-ctx-content-title">
          {name}
        </span>
      </div>

      {/* Tipo/Data, Pagamento, Status e Ações só existem nas linhas-filhas — a
          linha-pai apenas identifica o aluno. */}
      <div />
      <div />
      <div />
      <div />
    </button>
  );
}

/**
 * Tabela da pesquisa de Pagamento Individual: alunos como linhas-pai
 * (`GET /individualpayment/people`) e contratos financeiros como linhas-filhas
 * carregadas ao expandir (`GET /individualpayment/people/{personId}/courses`).
 *
 * Herda a linguagem visual de `EnrollmentTable` (mesma casca de card, escala de
 * grid, alturas de linha, chevron, seção de filhos em `bg-ctx-layout-body` e
 * rodapé). O rodapé usa o `Pagination` do ADS quando o backend informa
 * `totalPages`; sem esse dado, cai para anterior/próxima, porque uma paginação
 * numerada sem total mentiria (ver D3/D12 do design).
 */
export function IndividualPaymentTable({
  people,
  isLoading,
  isError,
  hasSearched,
  hasNextPage,
  hasPreviousPage,
  rangeFrom,
  rangeTo,
  totalCount,
  totalPages,
  pageIndex,
  onPageChange,
  onRetry,
  onContractAction,
}: IndividualPaymentTableProps) {
  const { t } = useTranslation('individualPayment');

  // As linhas abertas valem para a página em que foram abertas. Guardar a página
  // junto do conjunto faz a expansão zerar sozinha ao paginar (e em nova
  // pesquisa, que volta para a página 0) sem precisar de um efeito de reset.
  const [expansion, setExpansion] = useState<{
    readonly page: number;
    readonly ids: ReadonlySet<number>;
  }>({ page: pageIndex, ids: new Set() });

  const openIds = expansion.page === pageIndex ? expansion.ids : EMPTY_IDS;

  const toggle = (personId: number) => {
    const next = new Set(openIds);
    if (next.has(personId)) next.delete(personId);
    else next.add(personId);
    setExpansion({ page: pageIndex, ids: next });
  };

  if (isLoading) {
    return (
      <div className={CARD_CLASS}>
        <div className="overflow-x-auto">
          <div className={MIN_TABLE_WIDTH}>
            <TableHeader />
            <TableSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={CARD_CLASS}>
        <div className="overflow-x-auto">
          <div className={MIN_TABLE_WIDTH}>
            <TableHeader />
          </div>
        </div>
        <div className="flex flex-col items-center gap-mini p-xl">
          <p className="ts-paragraph-xs text-ctx-interactive-destructive">{t('table.error')}</p>
          <Button hierarchy="secondary" size="md" onClick={onRetry}>
            {t('table.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className={CARD_CLASS}>
        <div className="overflow-x-auto">
          <div className={MIN_TABLE_WIDTH}>
            <TableHeader />
          </div>
        </div>
        <div className="p-xl">
          {hasSearched ? (
            <EmptyState title={t('table.empty')} description={t('table.emptyHint')} />
          ) : (
            <IndividualPaymentSearchPrompt />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={CARD_CLASS}>
      <div className="overflow-x-auto">
        <div className={MIN_TABLE_WIDTH}>
          <TableHeader />
          {people.map(person => {
            const personId = person.id ?? 0;
            const isOpen = openIds.has(personId);
            return (
              <div key={personId}>
                <PersonRow person={person} isOpen={isOpen} onToggle={() => toggle(personId)} />
                <ContractRowsSection
                  personId={personId}
                  personName={person.name ?? ''}
                  isOpen={isOpen}
                  onContractAction={onContractAction}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-mini py-micro">
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {totalCount !== null ? (
            <>
              {rangeFrom}–{rangeTo} {t('table.of')}{' '}
              <strong className="text-ctx-content-base">{totalCount}</strong>{' '}
              {t('table.totalLabel')}
            </>
          ) : (
            t('table.range', { from: rangeFrom, to: rangeTo })
          )}
        </span>

        {totalPages !== null ? (
          totalPages > 1 && (
            <Pagination
              pageAmount={totalPages}
              activeIndex={pageIndex}
              setActiveIndex={onPageChange}
              ellipsisLabel="…"
            />
          )
        ) : (
          <div className="flex items-center gap-quark">
            <IconButton
              icon={ChevronLeftIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('table.previous')}
              disabled={!hasPreviousPage}
              onClick={() => onPageChange(pageIndex - 1)}
            />
            <IconButton
              icon={ChevronRightIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('table.next')}
              disabled={!hasNextPage}
              onClick={() => onPageChange(pageIndex + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
